import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles, relatedArticleSlugs } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import type { ArticleData } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article: ArticleData | undefined = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug],
  );

  const relatedArticles: ArticleData[] = useMemo(() => {
    const allExceptCurrent = articles.filter((a) => a.slug !== slug);
    const related = allExceptCurrent.filter((a) => relatedArticleSlugs.includes(a.slug));
    return related.length >= 3 ? related.slice(0, 3) : allExceptCurrent.slice(0, 3);
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://usfans.work';

  if (!article) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center px-4">
            <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">Artykuł nie znaleziony</h1>
            <p className="text-foreground-600 mb-6">Przepraszamy, szukany artykuł nie istnieje.</p>
            <Link to="/" className="inline-flex items-center px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors whitespace-nowrap">
              Powrót do strony głównej
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      author: {
        '@type': 'Organization',
        name: 'Redakcja USFans Polska',
      },
      datePublished: '2026-06-24',
      dateModified: '2026-06-24',
      publisher: {
        '@type': 'Organization',
        name: 'Redakcja USFans Polska',
        url: siteUrl,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/artykul/${article.slug}`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Strona główna',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: article.title,
          item: `${siteUrl}/artykul/${article.slug}`,
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      <SEOHead jsonLd={articleSchemas} />
      <Navbar />
      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="hover:text-accent-600 transition-colors whitespace-nowrap">Strona główna</Link>
              </li>
              <li className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li className="text-foreground-700 font-medium truncate">{article.title}</li>
            </ol>
          </nav>

          <header className="mb-10">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </span>
                Redakcja USFans Polska
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </span>
                Ostatnia aktualizacja: 24 czerwca 2026
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                Czas czytania: {article.readingTime}
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img
              src={article.img}
              alt={article.title}
              className="w-full h-auto max-h-[420px] object-cover"
              loading="eager"
            />
          </div>

          <div className="bg-background-100 rounded-2xl p-6 mb-10">
            <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Spis treści</h2>
            <ol className="space-y-2 text-sm text-foreground-600">
              {article.sections.map((section, i) => (
                <li key={i}>
                  <a href={`#section-${i + 1}`} className="hover:text-accent-600 transition-colors">
                    {i + 1}. {section.heading}
                  </a>
                </li>
              ))}
              {article.faqs.length > 0 && (
                <li>
                  <a href={`#section-faq`} className="hover:text-accent-600 transition-colors">
                    {article.sections.length + 1}. FAQ — Najczęściej zadawane pytania
                  </a>
                </li>
              )}
            </ol>
          </div>

          <div className="prose-custom space-y-10 text-foreground-700 leading-relaxed">
            {article.sections.map((section, i) => (
              <section key={i} id={`section-${i + 1}`}>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">{section.heading}</h2>
                {section.paragraphs.map((p, j) => (
                  <p key={j} className="mb-4">{p}</p>
                ))}
              </section>
            ))}

            {article.faqs.length > 0 && (
              <section id="section-faq">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">FAQ — Najczęściej zadawane pytania</h2>
                <div className="space-y-4">
                  {article.faqs.map((faq, i) => (
                    <div key={i} className="bg-background-100 rounded-xl p-5">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{faq.q}</h3>
                      <p className="text-sm">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          <div className="mt-12 text-center">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-8 py-4 text-base font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap"
            >
              Przejdź do USFans Spreadsheet
              <span className="w-5 h-5 flex items-center justify-center ml-2">
                <i className="ri-arrow-right-line"></i>
              </span>
            </a>
          </div>

          <div className="mt-14 pt-10 border-t border-background-200">
            <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">Powiązane artykuły</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((rel, i) => (
                <Link
                  key={i}
                  to={`/artykul/${rel.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200 transition-colors"
                >
                  <div className="h-36 overflow-hidden">
                    <img
                      src={rel.img}
                      alt={rel.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-sm font-semibold text-foreground-800 leading-snug line-clamp-2">{rel.title}</span>
                    <span className="block mt-2 text-xs text-accent-600 font-medium whitespace-nowrap">
                      Czytaj więcej →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <Link to="/" className="inline-flex items-center text-sm font-semibold text-accent-600 hover:text-accent-500 transition-colors whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-arrow-left-line"></i>
              </span>
              ← Powrót do strony głównej
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}